import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import type { ConditionRule, RuleType, ConditionCategory } from '../types';
import { RULE_TYPE_OPTIONS, OPERATOR_OPTIONS, CATEGORY_LABELS } from '../utils/constants';

/**
 * 2026-09-22：条件记录里 value 与 params 是两个存数字的字段，而下游读取口径不一
 * （sell_cooldown 读 value、daily_max_buys 读 params）。编辑框写的是 params，
 * 若 value 不跟着更新就会出现「改了不生效」或「显示 25 实际 6」。
 * 这里在保存时把 params 的主数值派生到 value，保证两边永远一致。
 * 后端 routes/conditions.ts 有同样的兜底映射，两边必须保持同步。
 */
const PRIMARY_PARAM_KEY: Record<string, string> = {
  sell_cooldown: 'days',
  loss_double: 'multiplier',
  consecutive_loss_double: 'factor',
  daily_max_buys: 'maxStocks',
  buy_sort_by: 'sortBy',
  screen_up_days: 'minUpDays',
  // price_amplitude 的阈值在顶层「阈值」输入框编辑，不由 params.pct 派生；
  // 保留映射会导致保存时用 params.pct（未更新）把用户输入的 value 覆盖回旧值
  listing_days: 'minDays',
  profit_threshold: 'threshold',
  loss_threshold: 'threshold',
  max_price: 'maxPrice',
  low_turnover_sell: 'threshold',
  screen_low_30d_max_pct: 'pct',
  loss_30d_low_stop_days: 'days',
  float_market_cap_max: 'maxCap',
  open_jump_cancel: 'pct',
  max_portfolio_value: 'maxValue',
  avg_down_buy: 'pct',
  avg_down_sell: 'pct',
  turnover_rate_consecutive: 'threshold',
  volume_ratio_consecutive: 'threshold',
  limit_up_history: 'minCount',
};

const valueFromParams = (rt: string, p: Record<string, any>): string | undefined => {
  const key = PRIMARY_PARAM_KEY[rt];
  if (!key || !p) return undefined;
  const v = p[key];
  return (v === undefined || v === null || v === '') ? undefined : String(v);
};

interface ConditionEditorProps {
  open: boolean;
  condition: ConditionRule | null;
  defaultCategory?: ConditionCategory;
  onClose: () => void;
  onSave: (condition: Partial<ConditionRule>) => Promise<void>;
}

/**
 * 条件编辑器（结构化表单）
 * 支持选择规则类型、设置参数、描述等
 */
export default function ConditionEditor({
  open,
  condition,
  defaultCategory = 'buy',
  onClose,
  onSave,
}: ConditionEditorProps) {
  const [category, setCategory] = useState<ConditionCategory>(defaultCategory);
  const [ruleType, setRuleType] = useState<RuleType>('turnover_rate_consecutive');
  const [operator, setOperator] = useState<string>('>=');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [params, setParams] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (condition) {
        setCategory(condition.category);
        setRuleType(condition.ruleType);
        setOperator(condition.operator || '>=');
        setValue(condition.value || '');
        setDescription(condition.description);
        setEnabled(condition.enabled);
        setParams(condition.params || {});
      } else {
        setCategory(defaultCategory);
        setRuleType('turnover_rate_consecutive');
        setOperator('>=');
        setValue('');
        setDescription('');
        setEnabled(true);
        setParams({});
      }
    }
  }, [open, condition, defaultCategory]);

  // 根据规则类型渲染额外参数输入
  const renderParams = () => {
    switch (ruleType) {
      case 'turnover_rate_consecutive':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="连续天数"
              type="number"
              value={params.consecutiveDays ?? 3}
              onChange={(e) => setParams({ ...params, consecutiveDays: parseInt(e.target.value) || 3 })}
              size="small"
              sx={{ width: 120 }}
            />
            <TextField
              label="换手率阈值(%)"
              type="number"
              value={params.threshold ?? 7}
              onChange={(e) => setParams({ ...params, threshold: parseFloat(e.target.value) || 7 })}
              size="small"
              sx={{ width: 140 }}
            />
          </Box>
        );
      case 'volume_ratio_consecutive':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="连续天数"
              type="number"
              value={params.consecutiveDays ?? 3}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                setParams({ ...params, consecutiveDays: Number.isFinite(v) ? v : 3 });
              }}
              size="small"
              sx={{ width: 120 }}
            />
            <TextField
              label="分母均量天数"
              type="number"
              value={params.lookback ?? 5}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                setParams({ ...params, lookback: Number.isFinite(v) ? v : 5 });
              }}
              size="small"
              sx={{ width: 140 }}
              helperText={(params.source ?? 'turnover') === 'volume'
                ? '量比 = 当日成交量 ÷ 过去 N 日均量（默认 5）'
                : '量比 = 当日换手率 ÷ 过去 N 日均换手率（默认 5）'}
            />
            <TextField
              select
              label="口径"
              value={params.source ?? 'turnover'}
              onChange={(e) => setParams({ ...params, source: e.target.value })}
              size="small"
              sx={{ width: 130 }}
              helperText="换手率=回测定稿口径（阈值 1.7）"
            >
              <MenuItem value="turnover">换手率比</MenuItem>
              <MenuItem value="volume">成交量比</MenuItem>
            </TextField>
            <TextField
              label="量比阈值"
              type="number"
              inputProps={{ step: 0.1 }}
              value={params.threshold ?? 3}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setParams({ ...params, threshold: Number.isFinite(v) ? v : 3 });
              }}
              size="small"
              sx={{ width: 140 }}
            />
          </Box>
        );
      case 'price_from_low':
        return (
          <TextField
            label="计算天数"
            type="number"
            value={params.days ?? 30}
            onChange={(e) => setParams({ ...params, days: parseInt(e.target.value) || 30 })}
            size="small"
            sx={{ width: 200 }}
          />
        );
      case 'price_amplitude':
        // 2026-10-09：天数改为 20/30/50 三档下拉。这个值同时是 **排序键**（buySortBy=2 振幅降序）
        //   的窗口天数，改它会影响每天实际买哪几只，不再只是判定阈值。
        return (
          <FormControl size="small" sx={{ width: 260 }}>
            <InputLabel>计算天数</InputLabel>
            <Select
              label="计算天数"
              value={[20, 30, 50].includes(Number(params.days)) ? Number(params.days) : 30}
              onChange={(e) => setParams({ ...params, days: Number(e.target.value) })}
            >
              <MenuItem value={20}>20 个交易日</MenuItem>
              <MenuItem value={30}>30 个交易日（Q2 定稿，推荐）</MenuItem>
              <MenuItem value={50}>50 个交易日</MenuItem>
            </Select>
            <FormHelperText>
              近 N 日累计振幅 = (maxHigh − minLow) / minLow × 100；同时作为「按振幅降序」排序键的窗口天数
            </FormHelperText>
          </FormControl>
        );
      case 'limit_up_history':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="统计月数"
              type="number"
              value={params.months ?? 6}
              onChange={(e) => setParams({ ...params, months: parseInt(e.target.value) || 6 })}
              size="small"
              sx={{ width: 120 }}
            />
            <TextField
              label="最少涨停次数"
              type="number"
              value={params.minCount ?? 1}
              onChange={(e) => setParams({ ...params, minCount: parseInt(e.target.value) || 1 })}
              size="small"
              sx={{ width: 160 }}
            />
          </Box>
        );
      case 'listing_days':
        return null; // 使用 value 字段
      case 'profit_threshold':
      case 'loss_threshold':
        return (
          <TextField
            label="阈值(%)"
            type="number"
            value={params.threshold ?? 5}
            onChange={(e) => setParams({ ...params, threshold: parseFloat(e.target.value) || 5 })}
            size="small"
            sx={{ width: 200 }}
          />
        );
      case 'market_breadth_pause':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="家数阈值"
              type="number"
              value={params.count ?? 3500}
              onChange={(e) => setParams({ ...params, count: parseInt(e.target.value) || 3500 })}
              size="small"
              sx={{ width: 140 }}
            />
            <TextField
              select
              label="方向"
              value={params.direction ?? 'down'}
              onChange={(e) => setParams({ ...params, direction: e.target.value })}
              size="small"
              sx={{ width: 140 }}
            >
              <MenuItem value="down">下跌家数</MenuItem>
              <MenuItem value="up">上涨家数</MenuItem>
            </TextField>
          </Box>
        );
      case 'market_index_pause':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="指数涨跌阈值(%)"
              type="number"
              value={params.pct ?? 1}
              onChange={(e) => setParams({ ...params, pct: parseFloat(e.target.value) || 1 })}
              size="small"
              sx={{ width: 160 }}
            />
            <TextField
              select
              label="方向"
              value={params.direction ?? 'down'}
              onChange={(e) => setParams({ ...params, direction: e.target.value })}
              size="small"
              sx={{ width: 140 }}
            >
              <MenuItem value="down">指数下跌</MenuItem>
              <MenuItem value="up">指数上涨</MenuItem>
            </TextField>
          </Box>
        );
      case 'exclude_st':
        return (
          <Typography variant="body2" color="text.secondary">
            此规则无需额外参数
          </Typography>
        );
      case 'held_filter':
        return (
          <Typography variant="body2" color="text.secondary">
            已持仓未卖出的股票不得入选/买入，无需额外参数
          </Typography>
        );
      case 'sell_cooldown':
        return (
          <TextField
            label="冷却交易日数"
            type="number"
            value={params.days ?? 30}
            onChange={(e) => {
              const days = parseInt(e.target.value) || 30;
              setParams({ ...params, days });
              setValue(String(days));
            }}
            size="small"
            sx={{ width: 200 }}
            helperText="止盈卖出后 N 个交易日内不得再次入选"
          />
        );
      case 'loss_double':
        return (
          <TextField
            label="资金倍数"
            type="number"
            value={params.multiplier ?? 2}
            onChange={(e) => {
              const m = parseFloat(e.target.value) || 2;
              setParams({ ...params, multiplier: m });
              setValue(String(m));
            }}
            size="small"
            sx={{ width: 200 }}
            helperText="止损卖出后再入选，按设定买入金额的 N 倍买入"
          />
        );
      case 'consecutive_loss_double':
        return (
          <TextField
            label="翻倍系数"
            type="number"
            value={params.factor ?? 2}
            onChange={(e) => {
              const f = parseFloat(e.target.value) || 2;
              setParams({ ...params, factor: f });
              setValue(String(f));
            }}
            size="small"
            sx={{ width: 200 }}
            helperText="连续亏损卖出后，每次买入金额 = 上次实际买入金额 × 系数（1万→2万→4万…）"
          />
        );
      case 'max_price':
        return (
          <TextField
            label="单价上限（元）"
            type="number"
            value={params.maxPrice ?? 100}
            onChange={(e) => {
              const v = parseFloat(e.target.value) || 100;
              setParams({ ...params, maxPrice: v });
              setValue(String(v));
            }}
            size="small"
            sx={{ width: 200 }}
            helperText="股价超过该值的股票不入选、也不买入"
          />
        );
      case 'same_day_sell_block':
        return (
          <Typography variant="body2" color="text.secondary">
            当日买入的股票当日不得卖出（T+1），无需额外参数
          </Typography>
        );
      case 'daily_max_buys':
        return (
          <TextField
            label="每日最多买入只数"
            type="number"
            value={params.maxStocks ?? 25}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              const max = Number.isFinite(v) ? Math.max(1, Math.min(50, v)) : 25;
              setParams({ ...params, maxStocks: max });
              setValue(String(max));
            }}
            size="small"
            sx={{ width: 200 }}
            helperText="候选股多于该值时，按 buy_sort_by 排序后只买前 N 只"
          />
        );
      case 'buy_sort_by':
        return (
          <TextField
            select
            label="撞顶时优先选"
            value={params.sortBy ?? 3}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              setParams({ ...params, sortBy: v });
              setValue(String(v));
            }}
            size="small"
            sx={{ width: 240 }}
            helperText="候选数 > maxStocks 时，按此规则排序后截断"
          >
            <MenuItem value={1}>1 - 换手率降序</MenuItem>
            <MenuItem value={2}>2 - 振幅降序</MenuItem>
            <MenuItem value={3}>3 - 综合分（换手+振幅，推荐）</MenuItem>
            <MenuItem value={4}>4 - 股票代码字典序（回测基线）</MenuItem>
            <MenuItem value={5}>5 - 综合分（换手×振幅）</MenuItem>
            <MenuItem value={6}>6 - 换手 + 0.5×振幅</MenuItem>
            <MenuItem value={7}>7 - 0.5×换手 + 振幅</MenuItem>
            <MenuItem value={8}>8 - 换手 + 2×振幅</MenuItem>
            <MenuItem value={9}>9 - 2×换手 + 振幅</MenuItem>
            <MenuItem value={10}>10 - 振幅升序（小→大）</MenuItem>
          </TextField>
        );
      case 'screen_up_days':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="计算天数（默认 3）"
              type="number"
              value={params.days ?? 3}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                setParams({ ...params, days: Number.isFinite(v) && v > 0 ? v : 3 });
              }}
              size="small"
              sx={{ width: 160 }}
            />
            <TextField
              label="最少上涨天数（默认 2）"
              type="number"
              value={params.minUpDays ?? 2}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                const n = Number.isFinite(v) && v >= 0 ? v : 2;
                setParams({ ...params, minUpDays: n });
                setValue(String(n)); // 与 value 同步，列表页可读；0 = 关闭该条件
              }}
              size="small"
              sx={{ width: 190 }}
              helperText="0 = 关闭；上涨 = 当日收盘价 > 前一交易日收盘价"
            />
          </Box>
        );
      case 'loss_30d_low_stop_days':
        return (
          <TextField
            label="计算天数（0=关）"
            type="number"
            value={params.days ?? 0}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              const days = Number.isFinite(v) && v >= 0 ? v : 0;
              setParams({ ...params, days });
              setValue(String(days));
            }}
            size="small"
            sx={{ width: 200 }}
            helperText="0=关闭；>0=按近N日最低价卖出"
          />
        );
      case 'screen_low_30d_max_pct':
        return (
          <TextField
            label="涨幅上限(%)（0=关）"
            type="number"
            value={params.pct ?? 0}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              const pct = Number.isFinite(v) && v >= 0 ? v : 0;
              setParams({ ...params, pct });
              setValue(String(pct));
            }}
            size="small"
            sx={{ width: 220 }}
            helperText="0=关闭；收盘≤近N日低点×(1+pct%)"
          />
        );
      case 'max_portfolio_value':
        return (
          <TextField
            label="持仓市值上限（元，0=不限）"
            type="number"
            value={params.maxValue ?? 0}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              const maxValue = Number.isFinite(v) && v >= 0 ? v : 0;
              setParams({ ...params, maxValue });
              setValue(String(maxValue));
            }}
            size="small"
            sx={{ width: 260 }}
            helperText="0=不限；持仓总市值+本笔>该值时跳过买入"
          />
        );
      case 'float_market_cap_max':
        return (
          <TextField
            label="流通市值上限（亿元，0=不限）"
            type="number"
            value={params.maxCap ?? 0}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              const maxCap = Number.isFinite(v) && v >= 0 ? v : 0;
              setParams({ ...params, maxCap });
              setValue(String(maxCap));
            }}
            size="small"
            sx={{ width: 240 }}
            helperText="0=不限；流通市值超过该值不入选"
          />
        );
      case 'open_jump_cancel':
        return (
          <TextField
            label="开盘涨幅取消(%)（0=关）"
            type="number"
            value={params.pct ?? 0}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              const pct = Number.isFinite(v) && v >= 0 ? v : 0;
              setParams({ ...params, pct });
              setValue(String(pct));
            }}
            size="small"
            sx={{ width: 220 }}
            helperText="0=关闭；买入时点较昨收涨幅>pct%时不买"
          />
        );
      case 'avg_down_buy':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="下跌触发(%)"
              type="number"
              value={params.pct ?? 5}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                const pct = Number.isFinite(v) && v > 0 ? v : 5;
                setParams({ ...params, pct });
                setValue(String(pct));
              }}
              size="small"
              sx={{ width: 160 }}
              helperText="较买入价跌≥pct%时触发补仓"
            />
            <TextField
              label="补仓金额（元，0=用默认买入金额）"
              type="number"
              value={params.amount ?? 0}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                const amount = Number.isFinite(v) && v > 0 ? v : 0;
                setParams({ ...params, amount });
              }}
              size="small"
              sx={{ width: 220 }}
              helperText="0=沿用 auto_trade_buy_amount"
            />
          </Box>
        );
      case 'avg_down_sell':
        return (
          <TextField
            label="补仓后上涨卖出(%)"
            type="number"
            value={params.pct ?? 2.5}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              const pct = Number.isFinite(v) && v > 0 ? v : 2.5;
              setParams({ ...params, pct });
              setValue(String(pct));
            }}
            size="small"
            sx={{ width: 200 }}
            helperText="较第二笔补仓价涨≥pct%时两笔全卖"
          />
        );
      case 'low_turnover_sell':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="连续低换手天数"
              type="number"
              value={params.days ?? 3}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                const days = Number.isFinite(v) && v > 0 ? v : 3;
                setParams({ ...params, days });
              }}
              size="small"
              sx={{ width: 160 }}
            />
            <TextField
              label="换手率上限(%)"
              type="number"
              value={params.threshold ?? 7}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                const threshold = Number.isFinite(v) && v > 0 ? v : 7;
                setParams({ ...params, threshold });
                setValue(String(threshold));
              }}
              size="small"
              sx={{ width: 160 }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const derived = valueFromParams(ruleType, params);
      const conditionData: Partial<ConditionRule> = {
        category,
        ruleType,
        operator: needsOperator() ? operator : undefined,
        value: derived !== undefined ? derived : (needsValue() ? value : undefined),
        params,
        description,
        enabled,
      };
      if (condition?.id) {
        conditionData.id = condition.id;
      }
      await onSave(conditionData);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const needsOperator = () => {
    return ['price_from_low', 'price_amplitude', 'listing_days', 'profit_threshold', 'loss_threshold'].includes(ruleType);
  };

  const needsValue = () => {
    // 2026-09-18：加入 max_price，保存时把 params.maxPrice 同步到 value，避免后端 value 与 params 不一致
    // 2026-09-22：加入 low_turnover_sell，同理同步 threshold
    // 2026-09-25：补上其余结构化条件的 value 同步，避免列表页显示空白或读 value 的下游拿不到值
    return [
      'price_from_low', 'price_amplitude', 'listing_days',
      'profit_threshold', 'loss_threshold', 'max_price', 'low_turnover_sell',
      'screen_low_30d_max_pct', 'loss_30d_low_stop_days', 'float_market_cap_max',
      'open_jump_cancel', 'max_portfolio_value', 'avg_down_buy', 'avg_down_sell',
    ].includes(ruleType);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{condition ? '编辑条件' : '新增条件'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* 分类 */}
          <TextField
            select
            label="条件分类"
            value={category}
            onChange={(e) => setCategory(e.target.value as ConditionCategory)}
            size="small"
            sx={{ width: 200 }}
          >
            <MenuItem value="buy">{CATEGORY_LABELS.buy}</MenuItem>
            <MenuItem value="sell">{CATEGORY_LABELS.sell}</MenuItem>
            <MenuItem value="global">{CATEGORY_LABELS.global}</MenuItem>
          </TextField>

          {/* 规则类型 */}
          <TextField
            select
            label="规则类型"
            value={ruleType}
            onChange={(e) => setRuleType(e.target.value as RuleType)}
            size="small"
            sx={{ width: 300 }}
          >
            {RULE_TYPE_OPTIONS
              .filter((opt) => opt.category.includes(category))
              .map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
          </TextField>

          {/* 运算符和值 */}
          {needsOperator() && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                label="运算符"
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                size="small"
                sx={{ width: 150 }}
              >
                {OPERATOR_OPTIONS.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="阈值"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                size="small"
                sx={{ width: 200 }}
              />
            </Box>
          )}

          {/* 额外参数 */}
          {renderParams()}

          {/* 描述 */}
          <TextField
            label="条件描述"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            placeholder="如：连续3日换手率>7%"
          />

          {/* 启用开关 */}
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="启用此条件"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || !description}>
          {loading ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
